import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, AlertCircle, FileText } from 'lucide-react';
import { useWarningDocument, useSignDocument } from '../hooks/useAgreementsAndResets';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { DEGREE_LABELS } from '../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from '@/shared/lib/toast';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useState } from 'react';

export function DocumentSignaturePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const { data: document, isLoading } = useWarningDocument(id);
  const signDocument = useSignDocument();

  // URL base sem o /api para servir arquivos estáticos
  const BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace('/api', '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!document) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-gray-500">Documento não encontrado</p>
          <Button onClick={() => navigate('/incident-management')} className="mt-4">
            Voltar para lista
          </Button>
        </div>
      </div>
    );
  }

  const handleSign = async () => {
    try {
      await signDocument.mutateAsync({
        id: document.id,
      });
      toast.success('Documento assinado com sucesso');
      navigate('/incident-management');
    } catch (error) {
      toast.error('Erro ao assinar documento');
    }
  };

  const isSigned = !!document.signedAt;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/incident-management')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Documento de Advertência</h1>
          <p className="text-muted-foreground">
            Gerado em {format(new Date(document.createdAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Document Content */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Documento de Advertência</CardTitle>
              {isSigned ? (
                <Badge variant="success" className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Assinado
                </Badge>
              ) : (
                <Badge variant="warning" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Aguardando Assinatura
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {document.pdfUrl ? (
              <div className="w-full" style={{ height: '800px' }}>
                <iframe
                  src={document.pdfUrl.startsWith('http') ? document.pdfUrl : `${BASE_URL}${document.pdfUrl}`}
                  className="w-full h-full border rounded-lg"
                  title="Documento de Advertência"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="w-16 h-16 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  O documento PDF está sendo gerado. Por favor, aguarde alguns instantes e recarregue a página.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signature Section */}
        <div className="space-y-6">
          {/* Document Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Informações do Documento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Colaborador</p>
                <p className="font-medium">{document.incident?.employee?.user.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Grau Aplicado</p>
                <Badge>
                  {document.incident?.appliedDegree
                    ? DEGREE_LABELS[document.incident.appliedDegree]
                    : 'N/A'}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Data de Geração</p>
                <p className="text-sm">
                  {format(new Date(document.createdAt), 'dd/MM/yyyy HH:mm')}
                </p>
              </div>
              {isSigned && (
                <>
                  <div className="pt-3 border-t">
                    <p className="text-sm text-muted-foreground">Data de Assinatura</p>
                    <p className="text-sm">
                      {format(new Date(document.signedAt!), 'dd/MM/yyyy HH:mm')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Assinado por</p>
                    <p className="text-sm font-medium">
                      {document.signatureData || document.signedBy?.user.name || 'N/A'}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Signature Form */}
          {!isSigned && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Assinar Documento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 border border-border rounded-md">
                  <div className="flex gap-2">
                    <AlertCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium mb-1">Importante</p>
                      <p className="text-muted-foreground">Ao assinar este documento, você confirma que:</p>
                      <ul className="list-disc list-inside mt-2 space-y-1 text-muted-foreground">
                        <li>Leu todo o conteúdo</li>
                        <li>Está ciente da ocorrência</li>
                        <li>Compreende as consequências</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-muted rounded-md">
                  <p className="text-sm text-muted-foreground mb-1">Assinatura será registrada como:</p>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Data e hora serão registradas automaticamente
                  </p>
                </div>

                <Button
                  onClick={() => setConfirmDialogOpen(true)}
                  disabled={signDocument.isPending}
                  className="w-full"
                >
                  {signDocument.isPending ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Assinando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Assinar Documento
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        title="Confirmar Assinatura"
        description="Ao assinar, você confirma que leu e está ciente do conteúdo deste documento. A assinatura será registrada em seu nome e não poderá ser desfeita. Deseja continuar?"
        confirmText="Sim, Assinar"
        cancelText="Cancelar"
        onConfirm={handleSign}
        variant="default"
        icon="check"
        isLoading={signDocument.isPending}
      />
    </div>
  );
}
